import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";

import Homepage from "./components/HomePage";
import PublicInfoPage from "./pages/PublicInfoPage";
import Home from "./pages/Home";
import MyActivity from "./pages/MyActivity";
import Spaces from "./pages/Spaces";
import Message from "./pages/Message";
import Settings from "./pages/Settings";
import AllSpaces from "./pages/AllSpaces";
import ArchivedSpaces from "./pages/ArchivedSpaces";
import Notes from "./pages/Notes";
import SpaceDetail from "./pages/SpaceDetail";
import EventDetail from "./pages/EventDetail";
import Discover from "./pages/Discover";
import Pulse from "./pages/Pulse";
import Alerts from "./pages/AlertsView";
import Saved from "./pages/Saved";
import PostDetail from "./pages/PostDetail";
import Archive from "./pages/Archive";
import ArchiveProfile from "./pages/ArchiveProfile";
import Sentinel from "./pages/Sentinel";
import GlobalSearch from "./pages/GlobalSearch";
import UserProfile from "./components/UserProfile";
import ActiveNotificationBridge from "./components/ActiveNotificationBridge";
import { getProfile } from "./api/profile";
import { applyStoredAppearance } from "./utils/theme";
import { IDENTITY_REQUIRED_EVENT, type IdentityIntent } from "./utils/identityGate";
import { LogIn, UserPlus, X } from "lucide-react";

const SESSION_WATCHDOG_INTERVAL_MS = 10000;

function clearExpiredAuthSession() {
  ["token", "authToken", "user", "userData", "globalIncognito"].forEach((key) => localStorage.removeItem(key));
}

export default function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const homeActive = location.pathname === "/home";
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(() => {
    return Boolean(localStorage.getItem("token"));
  });
  const [identityIntent, setIdentityIntent] = useState<IdentityIntent | null>(null);
  const publicPaths = useMemo(() => ["/home", "/archive", "/discover", "/pulse", "/post/", "/search", "/spaces/all", "/spaces/archives", "/spaces/", "/user/", "/manifesto", "/privacy", "/terms"], []);

  // ✅ APPLY SAVED THEME ON MOUNT (before any page renders)
  useEffect(() => {
    applyStoredAppearance();
  }, []);

  useEffect(() => {
    const showIdentityGate = (event: Event) => setIdentityIntent((event as CustomEvent<IdentityIntent>).detail);
    window.addEventListener(IDENTITY_REQUIRED_EVENT, showIdentityGate);
    return () => window.removeEventListener(IDENTITY_REQUIRED_EVENT, showIdentityGate);
  }, []);

  // ✅ RECHECK LOGIN STATE ON EVERY STORAGE CHANGE
  useEffect(() => {
    const checkAuth = () => {
      setIsLoggedIn(Boolean(localStorage.getItem("token")));
    };

    window.addEventListener("storage", checkAuth);
    checkAuth();

    return () => {
      window.removeEventListener("storage", checkAuth);
    };
  }, []);

  const expireSession = useCallback(() => {
    clearExpiredAuthSession();
    setIsLoggedIn(false);
    if (location.pathname !== "/login" && !publicPaths.some((path) => location.pathname === path || (path.endsWith("/") && location.pathname.startsWith(path)))) {
      navigate("/login", { replace: true });
    }
  }, [location.pathname, navigate, publicPaths]);

  const verifySession = useCallback(async () => {
    if (!localStorage.getItem("token")) {
      setIsLoggedIn(false);
      return;
    }

    try {
      await getProfile();
    } catch (error: any) {
      if (error?.status === 401 || error?.status === 403) {
        expireSession();
      }
    }
  }, [expireSession]);

  useEffect(() => {
    if (!isLoggedIn) return;
    let cancelled = false;
    let inFlight = false;

    const check = async () => {
      if (cancelled || inFlight) return;
      inFlight = true;
      try {
        await verifySession();
      } finally {
        inFlight = false;
      }
    };

    check();
    const interval = window.setInterval(check, SESSION_WATCHDOG_INTERVAL_MS);
    const onFocus = () => check();
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") check();
    };

    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [isLoggedIn, verifySession]);

  return (
    <>
    {isLoggedIn && <ActiveNotificationBridge />}
    {(isLoggedIn || homeActive) && <Home keepAliveActive={homeActive} />}
    <Routes>

      {/* -------- PUBLIC -------- */}
      <Route
        path="/login"
        element={isLoggedIn ? <Navigate to="/home" replace /> : <Homepage />}
      />

      <Route path="/manifesto" element={<PublicInfoPage page="manifesto" />} />
      <Route path="/privacy" element={<PublicInfoPage page="privacy" />} />
      <Route path="/terms" element={<PublicInfoPage page="terms" />} />

      <Route
        path="/"
        element={isLoggedIn ? <Navigate to="/home" replace /> : <Navigate to="/login" replace />}
      />

      <Route
        path="/archive"
        element={<Archive />}
      />

      <Route
        path="/archive/:slug"
        element={<ArchiveProfile />}
      />

      {/* -------- PROTECTED -------- */}
      <Route
        path="/home"
        element={<></>}
      />

      <Route path="/activity/comments" element={isLoggedIn ? <MyActivity key="comments" kind="comments" /> : <Navigate to="/login" replace />} />
      <Route path="/activity/emotions" element={isLoggedIn ? <MyActivity key="emotions" kind="emotions" /> : <Navigate to="/login" replace />} />

      <Route
        path="/notes"
        element={isLoggedIn ? <Notes /> : <Navigate to="/login" replace />}
      />

      <Route
        path="/message"
        element={isLoggedIn ? <Message /> : <Navigate to="/login" replace />}
      />

      <Route
        path="/settings"
        element={isLoggedIn ? <Settings /> : <Navigate to="/login" replace />}
      />

      <Route
        path="/spaces"
        element={isLoggedIn ? <Spaces /> : <Navigate to="/login" replace />}
      />

      <Route
        path="/spaces/all"
        element={<AllSpaces />}
      />

      <Route
        path="/spaces/archives"
        element={<ArchivedSpaces />}
      />

      <Route
        path="/discover"
        element={<Discover />}
      />

      <Route
        path="/pulse"
        element={<Pulse />}
      />

      <Route
        path="/signals"
        element={<Navigate to="/pulse" replace />}
      />

      <Route
        path="/alerts"
        element={isLoggedIn ? <Alerts /> : <Navigate to="/login" replace />}
      />

      <Route
        path="/saved"
        element={isLoggedIn ? <Saved /> : <Navigate to="/login" replace />}
      />

      <Route
        path="/post/:postId"
        element={<PostDetail />}
      />

      <Route
        path="/search"
        element={<GlobalSearch />}
      />

      <Route
        path="/sentinel"
        element={isLoggedIn ? <Sentinel /> : <Navigate to="/login" replace />}
      />

      <Route
        path="/spaces/:spaceId/events/:eventId"
        element={<EventDetail />}
      />

      <Route
        path="/spaces/:spaceId"
        element={<SpaceDetail />}
      />

      <Route
        path="/user/:username"
        element={<UserProfile />}
      />

      {/* -------- FALLBACK -------- */}
      <Route
        path="*"
        element={<Navigate to={isLoggedIn ? "/home" : "/login"} replace />}
      />
    </Routes>
    {identityIntent && (
      <div className="bs-identity-gate fixed inset-0 z-[100] flex items-end justify-center p-3 sm:items-center" role="presentation" onMouseDown={() => setIdentityIntent(null)}>
        <section className="bs-identity-panel w-full max-w-[27rem]" role="dialog" aria-modal="true" aria-labelledby="identity-required-title" onMouseDown={(event) => event.stopPropagation()}>
          <div className="bs-identity-signal" aria-hidden="true"><span /><i /><b /></div>
          <button type="button" onClick={() => setIdentityIntent(null)} className="bs-identity-close" aria-label="Close"><X size={17} /></button>
          <p className="bs-identity-kicker">Identity required</p>
          <h2 id="identity-required-title" className="bs-identity-title">Join the signal to {identityIntent.action}</h2>
          <p className="bs-identity-copy">Keep reading anonymously. Sign in only when you want your action to belong to you.</p>
          <div className="bs-identity-actions">
            <button type="button" onClick={() => { setIdentityIntent(null); navigate("/login?mode=login", { state: { returnTo: identityIntent.returnTo } }); }} className="bs-identity-action is-secondary"><LogIn size={16} /><span><strong>Sign in</strong><small>Return to your identity</small></span></button>
            <button type="button" onClick={() => { setIdentityIntent(null); navigate("/login?mode=signup", { state: { returnTo: identityIntent.returnTo } }); }} className="bs-identity-action is-primary"><UserPlus size={16} /><span><strong>Create identity</strong><small>Enter the conversation</small></span></button>
          </div>
        </section>
      </div>
    )}
    </>
  );
}
